import React, { useEffect, useState } from "react";
import { Button, Card, CardContent, Typography, Chip, Avatar } from "@material-ui/core";
import { Club } from "api/clubs";
import { Booking, BookingOptions, BookingType, getBookingOptions, patchInvitation } from "api/bookings";
import Markdown from "react-markdown";
import { formatter, markdownIfYoutubelink } from "utils/helpers";
import moment from "moment";
import { useSnackbar } from "notistack";
import styles from "./CalendarView.module.scss";
import i18n from "./i18n";

// prop types
interface CompanyBookingCellProps {
    booking: Booking,
    isStandardUser: boolean,
    timeOffset: number,
    onClick?: (e: React.MouseEvent) => void,
    club: Club,
    getCompanyBookings: (clubId: string) => void,
}

// booking cell in calendar view
const CompanyBookingCell = ({ booking, isStandardUser, timeOffset, onClick, club, getCompanyBookings }: CompanyBookingCellProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    const [isLoading, setLoading] = useState(false);
    const [options, setOptions] = useState<BookingOptions>();

    useEffect(() => {
        if (booking.id) {
            fetchOptions(booking.id);
        }
    }, []);

    // fetch options
    const fetchOptions = async (id: string) => setOptions(await getBookingOptions({ bookingId: id }));

    // styling
    const getStyle = ({ bookingStatus, bookingType }: Booking) => {
        if (bookingType === BookingType.Meetup) {
            return { border: `2px solid ${styles.bookingStandardFinalColor}` };
        }
        if (bookingStatus === "pending") {
            return { background: styles.bookingPendingColor };
        }
        if (bookingType === BookingType.Standard) {
            return { background: styles.bookingStandardColor };
        }
        if (bookingType === BookingType.Training) {
            return { background: styles.bookingTrainingColor };
        }
        return {};
    };

    // format date
    const formatDate = (date: string) => {
        const m = moment(date);
        if (m.isSame(moment(), "day")) return m.format(`[${i18n.today}] \u2022 H:mm`);
        if (m.isSame(moment().subtract(1, "day"), "day")) return m.format(`[${i18n.yesterday}] \u2022 H:mm`);
        return m.format("DD.MM.YYYY \u2022 H:mm");
    };

    // format participants
    const formatParticipants = (b: Booking) => {
        const joined = b.participants.map((p) => `${p.user.firstName} ${p.user.lastName}`).join(", ");
        const hidden = b.numTotalParticipants - b.participants.length;
        if (hidden === 0) return joined;
        return `${joined} ${i18n.andMore(hidden)}`;
    };

    const expectedPriceCents = booking.exclusiveCompanyPriceCents || 0;

    // clickable
    const isClickable = !(booking.bookingType === BookingType.Blocking && booking.blockingReason === "not-blocking" && isStandardUser);

    // answer booking invitation
    const answerBookingInvitation = async (accept: boolean) => {
        setLoading(true);
        try {
            await patchInvitation({
                accept,
                bookingId: booking!.id,
                clientLatitude: club.latitude,
                clientLongitude: club.longitude,
                expectedPriceCents,
            });
            getCompanyBookings(club!.id);
            enqueueSnackbar(i18n.answerInvitationSuccess(accept), { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // Tags
    const hasBookingCompanyExclusiveTag = booking.bookingTags.includes("company-exclusive");
    const hasBookingVideoStreamTag = booking.bookingTags.includes("video-stream");
    const hasBookingVideoConferenceTag = booking.bookingTags.includes("video-conference");
    const hasExclusiveCompanyClubId = booking.exclusiveCompanyClub?.id;

    // render
    return (
        <Card
            style={{
                pointerEvents: isClickable ? "auto" : "none",
                ...getStyle(booking!),
            }}
        >
            <CardContent className={styles.cardContent}>
                {booking.bookingType !== BookingType.Meetup && (
                    <>
                        <Typography variant="subtitle2">{booking!.description || i18n.bookingTypes[booking!.bookingType]}</Typography>
                        <Typography variant="caption" component="p" className="marginBottom">
                            {`${formatDate(booking!.startDate)} - ${moment(booking!.endDate).format("H:mm")}`}
                            {" \u2022 "}
                            {booking!.court.courtName}
                        </Typography>
                        {booking?.detailsDescription && (
                            <>
                                <Markdown linkTarget="_blank">{booking!.detailsDescription}</Markdown>
                            </>
                        )}
                        {booking?.privateDescription && (
                            <>
                                <Typography variant="body2" className={styles.fontBold}>{i18n.moreInformation}</Typography>
                                <Markdown linkTarget="_blank">{markdownIfYoutubelink(booking!.privateDescription)}</Markdown>
                            </>
                        )}
                        {booking?.galleryMediaUrls && (booking?.galleryMediaUrls).length > 0 && (
                            <Avatar src={booking?.galleryMediaUrls[0]} className="club_avatar marginBottom" />
                        )}

                        <Typography variant="caption" component="p">
                            {formatParticipants(booking!)}
                        </Typography>
                        {(hasBookingCompanyExclusiveTag || hasBookingVideoStreamTag || hasBookingVideoConferenceTag || hasExclusiveCompanyClubId) && (
                            <Typography variant="caption" component="p">
                                {hasBookingCompanyExclusiveTag && hasExclusiveCompanyClubId && (<Chip label={i18n.exclusiveTag} variant="outlined" className={styles.tagCompanyExclusive} />)}
                                {hasBookingCompanyExclusiveTag && !hasExclusiveCompanyClubId && (<Chip label={i18n.sharedTag} variant="outlined" className={styles.tagCompanyExclusive} />)}
                                {hasBookingVideoStreamTag && (<Chip label={i18n.virtualEvent["video-stream"]} variant="outlined" className={styles.tagVideoConference} />)}
                                {hasBookingVideoConferenceTag && (<Chip label={i18n.virtualEvent["video-conference"]} variant="outlined" className={styles.tagVideoConference} />)}
                            </Typography>
                        )}
                        {/* actions */}
                        {options && options.possibleOptions.includes("answer-invitation") && (
                            <>
                                <Button variant="contained" className={styles.acceptRejectBtn} onClick={() => answerBookingInvitation(true)} disabled={isLoading}>
                                    {i18n.accept(expectedPriceCents ? formatter.format(expectedPriceCents / 100) : "")}
                                </Button>
                                <Button variant="outlined" className={styles.acceptRejectBtn} onClick={() => answerBookingInvitation(false)} disabled={isLoading}>
                                    {i18n.decline}
                                </Button>
                            </>
                        )}
                        <Button variant="contained" className={styles.acceptRejectBtn} onClick={onClick} disabled={isLoading}>
                            {i18n.details}
                        </Button>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default CompanyBookingCell;
